<template>
  <div>
    <div class="row justify-content-center">
      <h2>MassBays Habitat Goals</h2>
    </div>
    <div class="row">
      <div class="col-12">
        <h5 class="water-quality-main">Summary</h5>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <p>Using information about historical conditions, MassBays has developed goals for salt marshes, eelgrass meadows, and tidal flats for the 44 estuaries encompassed by Ipswich Bay, Massachusetts Bay, and Cape Cod Bay. These goals are stated in terms of both habitat condition (“healthy”) and extent (acres or miles) with a target date of 2050. We encourage resource managers at the local, state, and federal level to use these goals for their own planning.</p>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <h5 class="water-quality-main">Background</h5>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <p>All National Estuary Programs (NEPs) are required under the Federal Clean Water Act to prepare Comprehensive Conservation and Management Plans that describe long-term goals for improvement of coastal ecosystems, and how they will be achieved. To meet this mandate, MassBays chose to use an approach called the Biological Condition Gradient, and worked with a team of researchers to carry out the following tasks:</p>
        <ul>
          <li>
            Categorize MassBays’ estuaries. MassBays’ region is made up of three Bays, and encompasses 50 cities and towns from Salisbury to Provincetown. To facilitate planning, we identified 68 watersheds, or assessment units, including 44 embayments (find out more about this effort). Then, to simplify development of habitat goals while acknowledging local conditions, we grouped the 44 embayments into Stressor-Resource Categories (determined by the degree of human impacts and the habitats present) and Ecotypes (determined by physical characteristics).
            <figure>
              <figcaption style="text-align: center; font-style: italic;">
                Categories, with characteristics and examples
              </figcaption>
              <img src="../../assets/imgs/stressor-resource_categories.jpg" alt="Stress-Resource Categories Descriptive Diagram" style="width:100%">
            </figure>
            <figure>
              <figcaption style="text-align: center; font-style: italic;">
                Ecotypes, with physical descriptions and examples of each
              </figcaption>
              <img src="../../assets/imgs/ecotypes.png" alt="Ecotypes physical descriptions and examples" style="width:100%">
            </figure>
          </li>
          <li>
            <p>
              <b>Examine historical habitat extent and condition.</b> A team from U.S. EPA used maps from the 1770s to the present to assess changes in habitat extent for our entire planning area. In some cases, coastal change would preclude returning to those conditions (the filling of Boston Harbor tidelands to create the modern waterfront is the most dramatic example), but in others this information provided valuable insights for our goal-setting.
            </p>
            <figure>
              <figcaption style="text-align: center; font-style: italic;">
                The Biological Condition Gradient: A way to identify future opportunities based on historical conditions.
              </figcaption>
              <img src="../../assets/imgs/biological_conditions_gradient.jpg" alt="Ecotypes physical descriptions and examples" style="width:100%">
            </figure>
            <figcaption style="text-align: center; font-style: italic; font-size: 10px">
              Illustration: Emily Shumchenia/Integration and Application Network, <a href="ian.umces.edu/media-library">ian.umces.edu/media-library</a>
              </figcaption>
          </li>
          <li>
            <b>Collect information on local priorities.</b> Each coastal habitat offers unique benefits to people as well as the creatures that rely on those habitats for food and protection. MassBays asked local experts – residents of representative communities – which benefits they consider most important as we look toward the future.
          </li>
          <li>
            <b>Consult with scientists on the potential for future restoration.</b> Multiple factors influence habitat restoration potential. Sea level rise, coastal erosion, and temperature changes associated with climate change; development that hems in salt marshes and shoreline hardening with seawalls; water pollution; invasive species; dredging; and poor fishing and boating practices all impact the ability of coastal habitats to thrive. These factors were taken into account when setting out possibilities for 2050.
          </li>
        </ul>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <h5 class="water-quality-main">Interpreting the habitat goals included in the Ecohealth Tracking Tool</h5>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <p>
          For the 68 assessment areas in the MassBays region, the Ecohealth Tracking Tool provides information about changes in habitat extent (acres) over time in relation to an established goal. Some items to note as you explore:
        </p>
        <ul>
          <li>
            <b>The ETT currently displays habitat extent, not condition.</b> MassBays’ habitat goals consist of both condition (healthy habitats) and extent (acres or miles). Currently there are limited data and no standardized methods available to characterize the actual habitat health in a consistent way. Resource managers and researchers around New England are working on this problem.
          </li>
          <li>
            <b>The ETT displays the best available data.</b> Eelgrass, salt marsh and tidal flat habitat goals are based on the earliest comprehensive coastwide survey data we have available, which are from 1995 and 2005. Further, they are derived from remote-sensing data (e.g., aerial imagery collected via airplane) which are limited in terms of image quality, accuracy, and habitat visibility. Ideally our goals would be informed by site-specific habitat suitability models which are not available for MassBays’ region. We intend to support, assess, and ultimately incorporate such models into future goal-setting.
          </li>
          <li>
            <b>Not all areas have habitat-specific goals.</b> This is for one of two reasons:<br>
            <ol>
              <li>
                The area is not categorized as an embayment. We will be developing goals for inter-estuarine areas (stretches of coastline with straight rocky shores or barrier beaches) in the future.
              </li>
              <li>
                In some embayments, we lack historical mapping data on which to base a goal – even in embayments where we think habitats existed historically. For example, there are anecdotal accounts of eelgrass in the Merrimack River from the early 20th century that were not recorded during comprehensive mapping in 1995.
              </li>
            </ol>
          </li>
          <li>
            <b>Goals for salt marsh and tidal flat extent are equal to “current” acreage as of June 2005.</b> Due to sea level rise and existing development and infrastructure encroaching along the coastline, it is unclear whether that habitat has the potential to expand, so MassBays’ goals for salt marsh and tidal flats are focused on maintaining and improving the health of existing habitat rather than expansion. While researchers work to develop ways to reliably assess habitat health, MassBays has plans to develop and display Habitat Potential Indices (HPIs) in the ETT. HPIs are a means to evaluate whether existing water quality conditions support healthy habitats.
          </li>
          <li>
            <b>Some areas are already meeting or exceeding habitat goals.</b> MassBays set the habitat goals using a process that looks across embayment ecotypes (described above) in an effort to compare like-with-like. Individual embayments might be in better condition compared to similarly categorized embayments. In addition, habitat maps are a snapshot of habitat extent, and change in area can shift dramatically from year to year. Not to mention the limitations of remote sensing data and the potential that some habitats were missed in some survey years. In any case, meeting or exceeding a goal for habitat extent does not mean there is no work to do in your area! Reducing pollution through better stormwater management, for example, is needed throughout the Bays to improve habitat health.
          </li>
          <li>
           <b>Diadromous fish habitat goals</b> are based on existing and potential fish <b>migratory habitat</b> and <b>spawning habitat</b> extents, as determined by the Massachusetts Division of Marine Fisheries (DMF):
           <ul>
            <li>
              The ETT displays existing migratory habitat (river miles) and spawning habitat (acres) based on areas where DMF has determined that such habitat for river herring is either fully or partially accessible.
              <ul>
                <li>
                  Although the boundaries of the MassBays assessment areas in the ETT are based on extent of tidal influence, diadromous fish habitat often extends well beyond these boundaries to freshwater portions of rivers, lakes, and ponds. These freshwater areas are included in the ETT diadromous fish habitat data.
                </li>
                <li>
                  In cases where a MassBays assessment area receives flow from one or more upstream assessment areas, the calculation of existing and potential migratory and spawning habitat includes all habitat that is either within or upstream of the selected assessment area.
                </li>
              </ul>
            </li>
            <li>
              The ETT also displays fish migratory habitat and spawning habitat as currently “not accessible” to river herring in areas where DMF has determined that future habitat restoration is feasible.
            </li>
            <li>
              Diadromous fish habitat goals for 2050 include (1) areas where <b>new access</b> to habitat may be established, and (2) areas where <b>habitat improvements</b> may be implemented in areas that currently have only partial access for river herring.
            </li>
            <li>
              The 2050 goals are based on implementation of potential diadromous fish habitat restoration projects in the MassBays study area which are in the top 25% of prioritization scoring by DMF.  For more information on diadromous fish habitat restoration projects and DMF prioritization, see the <a href="https://www.mass.gov/info-details/diadromous-fisheries-project" target="_blank">DMF Diadromous Fisheries Project website</a>.
            </li>
           </ul>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { imageLibraryHabitat } from '../../lib/constants'
export default {
  data () {
    return {
        imageLibraryHabitat: imageLibraryHabitat
    }
  }
}
</script>

<style scoped>

</style>
